import AudienceHubPage from "@/components/AudienceHubPage";
import { IMG, TOK_ASSETS } from "@/lib/data";
import { STATUS } from "@/lib/status";

const PROGRAMMES = [
  {
    id: "interactive-playground",
    title: "Interactive Playground",
    desc: "A projection-mapped sensory playground for 0–7 year olds. Sound, light and movement.",
    image: IMG.playground,
    focal: "50% 30%",
    status: STATUS.LIVE_NOW,
    href: "/interactive-playground",
    eyebrow: "0–7 · Live now",
  },
  {
    id: "after-school-clubs",
    title: "After School Clubs",
    desc: "Capoeira, mask making, T-shirt design, drumming and creative tech — for ages 5–14.",
    image: IMG.dance1,
    focal: "50% 40%",
    status: STATUS.BOOKING_OPEN,
    href: "/after-school-clubs",
    eyebrow: "5–14 · Booking open",
  },
  {
    id: "home-education",
    title: "Home Education Programme",
    desc: "A Wednesday + Thursday programme for home-educating families. Heritage, XR and creative play.",
    image: IMG.heroPortal,
    focal: "center",
    status: STATUS.BOOKING_OPEN,
    href: "/home-education",
    eyebrow: "6–16 · Booking open",
  },
  {
    id: "beats-and-bubbles",
    title: "Beats & Bubbles",
    desc: "Toddler music + dance session with TOK soundtrack and bubbles. Calm, immersive and joyous.",
    image: IMG.dance2,
    focal: "50% 30%",
    status: STATUS.REGISTER_INTEREST,
    eyebrow: "0–4 · Register interest",
  },
  {
    id: "boogie-bounce",
    title: "Boogie Bounce",
    desc: "A movement and rhythm session for under-5s — Afro-beat soundscape, soft play and projection.",
    image: IMG.playground,
    focal: "50% 50%",
    status: STATUS.REGISTER_INTEREST,
    eyebrow: "0–4 · Register interest",
  },
  {
    id: "under-5-immersive-theatre",
    title: "Under 5's Immersive Theatre",
    desc: "Story-led immersive theatre designed for the very youngest audiences — sensory and safe.",
    image: IMG.immersiveNights,
    focal: "50% 40%",
    status: STATUS.REGISTER_INTEREST,
    eyebrow: "0–4 · Register interest",
  },
  {
    id: "puppet-show",
    title: "Puppet Show",
    desc: "Hand-crafted puppet theatre rooted in diaspora folklore — for families with children 3–10.",
    image: IMG.redDancer,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
    eyebrow: "3–10 · In development",
  },
  {
    id: "sensory-pool-party",
    title: "Sensory Pool Party",
    desc: "Soft-play meets sensory immersion — a family event for neurodiverse children and siblings.",
    image: IMG.wellness,
    focal: "50% 45%",
    status: STATUS.COMING_SOON,
    eyebrow: "All ages · Coming soon",
  },
];

export default function Family() {
  return (
    <AudienceHubPage
      testIdRoot="family"
      pageSource="family"
      seo={{
        title: "Family Workshops & Toddler Activities in Medway, Kent & London — Temple of Kulture",
        description:
          "Immersive family experiences from 0–14 in Medway, Kent and London. Beats & Bubbles, Boogie Bounce, sensory rooms, Interactive Playground and more.",
        image: IMG.playground,
      }}
      hero={{
        eyebrow: "For Families · 0–14",
        headline: "Worlds your child walks inside.",
        subhead:
          "Sensory rooms, immersive playgrounds, family socials and weekend rituals — built for every age, rooted in diaspora storytelling.",
        image: IMG.playground,
        focal: "50% 30%",
        primaryCta: { label: "See family events", to: "/whats-on?filter=experiences" },
      }}
      identity={{
        eyebrow: "Why families",
        headline: "Cultural play for the next generation.",
        body:
          "We design family experiences that don't talk down to children. They're cinematic, sensory and rooted in the cultures that shape who your child becomes. Babies, toddlers, school-age and tweens all have a home in the Temple.",
      }}
      programmes={PROGRAMMES}
      trustLine="Family programmes delivered across Medway, Kent and London — in partnership with local schools, libraries and community spaces. Free and pay-what-you-can sessions available."
      faqs={[
        {
          q: "What ages are your family programmes for?",
          a: "We run dedicated programmes from 0 to 14. Interactive Playground is 0–7, After School Clubs are 5–14, Home Education is 6–16, and we have Beats & Bubbles / Boogie Bounce for under-5s.",
        },
        {
          q: "Where do family sessions take place?",
          a: "Most run in our Medway HQ, with additional pop-ups in Kent and London partner venues. Specific event locations are listed on each event page.",
        },
        {
          q: "Are sessions accessible for neurodiverse children?",
          a: "Yes. Every session has a low-stimulus option and our Sensory Pool Party is built specifically with neurodiverse children in mind. Get in touch if you'd like to talk through accommodations.",
        },
        {
          q: "Are sessions free or paid?",
          a: "We offer a mix — some sessions are free, some are pay-what-you-can, and others are ticketed. We never want cost to be a barrier; if it is, please write to us.",
        },
      ]}
      finalCta={{
        eyebrow: "Be first to know",
        headline: "Family programmes drop monthly.",
        body: "Join the WhatsApp Channel for instant family event releases, taster days and half-term workshops.",
        label: "Join the Channel",
        to: "/join",
        bullets: ["Free taster days", "Half-term workshops", "Sensory-aware sessions"],
      }}
      stayWhatsAppCopy={{
        headline: "STAY CLOSE TO THE PLAYGROUND",
        tagline: "Family event drops, session releases and free taster days — the moment they're announced.",
        bullets: [
          "Family session releases",
          "Free taster days and open studios",
          "Half-term workshops and play labs",
          "Sensory-aware family events",
          "Parent community calls",
        ],
      }}
    />
  );
}
